import React from 'react';
import PropTypes from 'prop-types';

import { CustomizableLink } from '~components/common/Link';
import phoneToE164 from '../../utils/phoneToE164';

import { LEOAddresses as LEOAddressesProp } from './types';
import ContactList from './ContactList';
import StreetAddress from './StreetAddress';
import dedupeWebsites from './dedupeWebsites';
import dedupeAddresses from './dedupeAddresses';

function AddressList({ addresses, prefix, hours }) {
  if (addresses.length === 0) {
    return <p>Sorry, we don&apos;t have an address for this office.</p>;
  }

  // We always show the physical addresses. If there are no physical
  // addresses, we show a mailing address.

  const physicalAddresses = addresses.filter((addr) => addr.is_physical);
  const mailingAddresses = addresses.filter((addr) => !addr.is_physical);

  let addressesToShow;
  if (physicalAddresses.length > 0) {
    addressesToShow = physicalAddresses;
  } else {
    addressesToShow = [mailingAddresses[0]];
  }

  return addressesToShow.map((addr) => (
    <React.Fragment key={addr.address}>
      <h3 className='text-lg font-bold'>
        {addr.is_physical
          ? 'Physical Address'
          : 'Mailing Address (Cannot Receive Express Mail)'}
      </h3>
      <StreetAddress {...addr} hours={hours} prefix={prefix} />
    </React.Fragment>
  ));
}

AddressList.propTypes = {
  addresses: LEOAddressesProp,
  prefix: PropTypes.string,
  hours: PropTypes.string,
};

export default function LEOAddresses({ addresses, hours }) {
  return (
    <div className='mt-10 space-y-7'>
      <ContactList
        title='Website'
        titlePlural='Websites'
        items={dedupeWebsites(addresses.map((addr) => addr.website))}
      >
        {(url) => (
          <CustomizableLink href={url} target='_blank' rel='noopener'>
            {url}
          </CustomizableLink>
        )}
      </ContactList>
      <div className='divider' />
      <ContactList
        title='Email'
        titlePlural='Emails'
        items={addresses.map((addr) => addr.email)}
      >
        {(email) => (
          <CustomizableLink href={`mailto:${email}`}>{email}</CustomizableLink>
        )}
      </ContactList>
      <div className='divider' />

      <ContactList
        title='Phone number'
        titlePlural='Phone numbers'
        items={addresses.map((addr) => addr.phone)}
      >
        {(phone) => (
          <CustomizableLink href={`tel:${phoneToE164(phone)}`}>
            {phone}
          </CustomizableLink>
        )}
      </ContactList>
      <div className='divider' />
      <ContactList
        title='Fax number'
        titlePlural='Fax numbers'
        items={addresses.map((addr) => addr.fax)}
      >
        {(fax) => <span>{fax}</span>}
      </ContactList>
      <div className='divider' />
      <h2 className='mb-7 text-2xl font-semibold'>Voter registration</h2>
      <AddressList
        hours={hours}
        addresses={dedupeAddresses(
          addresses.filter((addr) => addr.process_domestic_registrations)
        )}
        prefix='Attn: Voter Registration Clerk'
      />
      <div className='divider' />
      <h2 className='mb-7 text-2xl font-semibold'>
        Absentee ballots / Vote by mail
      </h2>
      <AddressList
        hours={hours}
        addresses={dedupeAddresses(
          addresses.filter((addr) => addr.process_absentee_requests)
        )}
        prefix='Attn: Absentee Ballot / VBM Clerk'
      />
      <div className='divider' />
      <h2 className='mb-7 text-2xl font-semibold'>
        Overseas and military voters
      </h2>
      <AddressList
        hours={hours}
        addresses={dedupeAddresses(
          addresses.filter((addr) => addr.process_overseas_requests)
        )}
        prefix='Attn: UOCAVA Clerk'
      />
    </div>
  );
}

LEOAddresses.propTypes = {
  addresses: LEOAddressesProp,
  hours: PropTypes.string,
};
